import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';

import { MapOptionsInfo, MarkerManaged, PolygonManaged, PolygonInfo, MarkerInfo,
         MakerCoordenate, EventPositionMarker, EventClickMarker, CoordenateInfo,
         ContentWindowInfo } from './interfaces';

import { Loader } from '@googlemaps/js-api-loader';

import { MarkersManager } from './MarkersManager';
import { PolygonsManager } from './PolygonsManager';

import {GeocodeManager} from './GeocodeManager';
         

class MapService 
{  
    public  version:string= 'MapService.v202607061629';

    public markersManager: MarkersManager|null=null;
    public polygonsManager: PolygonsManager|null=null;
    public geocodeManager:GeocodeManager |null=null;

    private optionsInfo: MapOptionsInfo;
    public map: google.maps.Map | null = null;
    //private element: string | HTMLElement;

    static DEFAULT_CENTER = { lat: -34.6037, lng: -58.3816 };
    static DEFAULT_ZOOM = 13;

    constructor( optionsInfo: MapOptionsInfo )  //element: string | HTMLElement,
    {
        if(!optionsInfo) throw new Error('API Key is required');

        if (!optionsInfo.apiKey)  throw new Error('API Key is required');       

        this.optionsInfo=optionsInfo;

        this.markersManager = new MarkersManager( this.optionsInfo.clusterable || false  );
        this.polygonsManager = new PolygonsManager( );
    }

    public async InitializeMapServiceAsync(markersInfo:MarkerInfo[]|undefined, polygonsInfo:PolygonInfo[]|undefined ): Promise<void>
    {
        try 
        {
//            this.purgeGoogleMaps();
            MapService.ForceReloadGoogleMaps();


            let loader: Loader  = new Loader({  apiKey: this.optionsInfo.apiKey,
                                                version: 'weekly',
                                                libraries: ['marker','places', 'geometry'] // 'drawing' eliminada: removida en Maps JS API v3.65 (DrawingManager). El dibujo se instancia lazy en PolygonsManager.EnabledDrawingAsync
            });

            if (!loader) return;

            //const { Map } = await loader.importLibrary('maps') as google.maps.MapsLibrary;

            const [mapslib, markerLib] = await Promise.all([
                loader.importLibrary('maps') ,
                loader.importLibrary('marker')
            ]);

            const mapElement = typeof this.optionsInfo.idMaps === 'string' ? document.getElementById(this.optionsInfo.idMaps) : this.optionsInfo.idMaps;

            if (!mapElement) throw new Error('Map container not found');

            let center = this.optionsInfo.center || MapService.DEFAULT_CENTER;

            this.map = new google.maps.Map(mapElement, 
            {                
                center: { lat:center?.lat||0, lng:center?.lng||0 },
                zoom: this.optionsInfo.zoom || MapService.DEFAULT_ZOOM,                
                mapId: this.optionsInfo.idMaps || 'DEMO_MAP_ID',
                renderingType: google.maps.RenderingType.RASTER
                //mapId: this.optionsInfo.idMaps ? this.optionsInfo.idMaps : undefined //'map'
            });

            // iniciando el manejador de markers y sus marcadores iniciales

            if(this.markersManager)
            {
                this.markersManager.SetMap( this.map );

                if(!markersInfo) markersInfo=[];

                this.markersManager.isLoadingMarkers = true;// usado para reducir efecto carga individual
                markersInfo.forEach( async markerInfo => 
                {
                    this.markersManager?.AddMarkerAsync(markerInfo); 
                });
                this.markersManager.ShowAllMarkers();
                this.markersManager.isLoadingMarkers = false;
            }

            // iniciando el manejador de polygons con sus poligonos
            if(this.polygonsManager)
            {
                this.polygonsManager.SetMap( this.map );
                
                if(!polygonsInfo) polygonsInfo=[];

                this.polygonsManager.isLoadingPolygons = true;
                polygonsInfo.forEach( async polygonInfo => 
                {
                    this.polygonsManager?.AddPolygonAsync(polygonInfo); 
                });
                this.polygonsManager.ShowAllPolygons();
                this.polygonsManager.isLoadingPolygons = false;
            }
            
            console.log(this.version);
        } 
        catch (error) 
        {
            console.error('Error initializing map:', error);
            throw error;
        }
    }

    public static async ForceReloadGoogleMaps(): Promise<void>
    {
        const win = window as any;

        // 1. Remover scripts que Google inyectó
        document.querySelectorAll('script[src*="maps.googleapis.com"]')
            .forEach(s => s.remove());

        // 2. Remover styles que Google inyectó
        document.querySelectorAll('style').forEach(s => {
            if (s.textContent?.includes('gm-style'))
                s.remove();
        });

        // 3. Resetear el estado interno del Loader
        // @googlemaps/js-api-loader guarda esto internamente
        if (win.google?.maps) {
            // Guardar la API key antes de limpiar
            const callbacks = win.__googlemapsapi;
            delete win.__googlemapsapi;
            delete win.google.maps;
            
            // Si google.maps es lo único en google, limpiar todo
            if (Object.keys(win.google).length === 0)
                delete win.google;
        }

        // 4. Limpiar callbacks pendientes del Loader
        const callbackKeys = Object.keys(win).filter(k =>
            k.startsWith('__googlemaps') || 
            k.startsWith('__googleMaps') ||
            k === 'initMap' ||
            k === 'gm_authFailure'
        );
        callbackKeys.forEach(k => delete win[k]);

        // 5. Limpiar el estado interno del Loader de npm
        // La clase Loader guarda instancias previas en una propiedad estática
        try {
            const { Loader } = await import('@googlemaps/js-api-loader');
            // Resetear el singleton interno del Loader
            (Loader as any).instance = undefined;
            (Loader as any).apiKey = undefined;
            (Loader as any).promise = undefined;
            (Loader as any).LOADING = undefined;
        } catch(e) {
            console.warn('[MapService] No se pudo resetear Loader instance:', e);
        }

        console.log('[MapService] Google Maps Loader forzado a reload');
    }
    /*
    public static ResetGoogleMapsLoader(): void
    {
        const win = window as any;
        
        // @googlemaps/js-api-loader guarda su estado en esta clave
        // El nombre exacto puede variar según la versión
        const loaderKeys = Object.keys(win).filter(k => 
            k.includes('google') || k.includes('GoogleMaps') || k.includes('gmap')
        );
        
        console.log('[MapService] Loader keys encontradas:', loaderKeys);
        loaderKeys.forEach(k => {
            console.log(`  ${k}:`, typeof win[k]);
        });
        
        // Remover solo los scripts y styles de Maps
        document.querySelectorAll('script[src*="maps.googleapis.com"]')
            .forEach(s => s.remove());
            
        document.querySelectorAll('style').forEach(s => {
            if (s.textContent?.includes('gm-style'))
                s.remove();
        });
    }
    */

    /*
    public static ResetGoogleMapsLoader(): void
    {
        try {
            // Limpiar el estado interno del Loader de @googlemaps/js-api-loader
            // que guarda si ya cargó la librería
            const win = window as any;
            
            // El Loader guarda el estado en estas propiedades
            delete win.google?.maps;
            delete win.google;
            
            // Limpiar los callbacks internos del Loader
            delete win.__googleMapsCallback;
            delete win.__googleMapsApiCallback;
            
            // Limpiar el estado del Loader de la librería npm
            // accediendo a sus propiedades estáticas internas
            const loaderModule = win.__googleMapsLoaderState;
            if (loaderModule) delete win.__googleMapsLoaderState;

            
            // Remover el script tag que Google inyectó
            document.querySelectorAll('script[src*="maps.googleapis.com"]')
                .forEach(s => s.remove());

            // Remover los style tags que Google inyectó
            document.querySelectorAll('style')
                .forEach(s => {
                    if (s.textContent?.includes('gm-style'))
                        s.remove();
                });
            console.log('[MapService] Google Maps Loader reseteado');
        } catch(e) {
            console.warn('[MapService] Error reseteando Loader:', e);
        }
    }
    */

    public async UpdateMarkerAsync(markerInfo:MarkerInfo): Promise<void> 
    {
        if (!this.map || !this.markersManager) return;

        let marker = this.markersManager?.GetMarkerInfo(markerInfo.id);

        if(!marker)
        { 
            console.log('Adding new marker with id:', markerInfo.id);
            await this.markersManager?.AddMarkerAsync(markerInfo);          
        }
        else
        {
            console.log('Updating marker with id:', markerInfo.id );
            await this.markersManager?.UpdateMarkerAsync(markerInfo);
        }
    }

    public async UpdateMarkersAsync( markersInfo:MarkerInfo[] ): Promise<void> 
    {
        if (!this.map || !this.markersManager) return;

        //mecanismo para mitigar el efecto de carga individual al mostrar los markers. 
        //asigna el mapa al final
        this.markersManager.isLoadingMarkers = false;
        markersInfo.forEach( markerInfo => this.UpdateMarkerAsync(markerInfo) );        
        this.markersManager.isLoadingMarkers = true;
        this.markersManager.ShowAllMarkers();
    }

    public async ShowInfoWindowAsync(contentInfoWindow:ContentWindowInfo): Promise<void>
    {           
        if (!this.map || !this.markersManager) return;

        this.markersManager.ShowInfoWindow(contentInfoWindow);
    }

    public async UpdatePolygonAsync(polygonInfo:PolygonInfo): Promise<void> 
    {
        if ( !this.map || !this.polygonsManager) return;

        let polygon= this.polygonsManager.GetPolygonInfo(polygonInfo.id);

        if(!polygon)
        { 
            console.log('Adding new marker with id:', polygonInfo.id);
            await this.polygonsManager?.AddPolygonAsync(polygonInfo);          
        }
        else
        {
            console.log('updating marker with id:',polygon.id );
            this.polygonsManager?.UpdatePolygon(polygonInfo);
        }
    }

    public async UpdatePolygonsAsync(polygonsInfo:PolygonInfo[]): Promise<void> 
    {
        if (!this.map || !this.polygonsManager) return;

        this.polygonsManager.isLoadingPolygons = false;
        polygonsInfo.forEach( polygonInfo => this.UpdatePolygonAsync( polygonInfo ) );
        this.polygonsManager.isLoadingPolygons = true;
        this.polygonsManager.ShowAllPolygons();
    }

    public async DisposeAsync(): Promise<void>
    {
        try 
        {
            await this.markersManager?.DisposeAsync();
            await this.polygonsManager?.DisposeAsync();
            
            if(!this.map) return;

            this.map=null;
            
        } 
        catch (error) 
        {
            console.error('Error disposing map:', error);
            throw error;
        }
    }

    public purgeGoogleMaps():void
    {
        console.log('[purgeGoogleMaps] Iniciando limpieza completa...');

        // 1. Scripts de Google Maps
        document.querySelectorAll('script').forEach(script => {
            if (script.src.includes('maps.googleapis.com') || 
                script.src.includes('maps.google.com') ||
                script.id === '__googleMapsScriptId') {
                script.remove();
                console.log('[purgeGoogleMaps] Script removido:', script.src);
            }
        });

        // 2. Style tags inyectados por Google Maps
        document.querySelectorAll('style').forEach(style => {
            if (style.textContent?.includes('gm-style') || 
                style.textContent?.includes('googlemaps') ||
                style.getAttribute('nonce') !== null && style.textContent?.includes('.gm')) {
                style.remove();
                console.log('[purgeGoogleMaps] Style tag removido');
            }
        });

        // 3. Nodos DOM del InfoWindow que Google deja en el body
        const gmSelectors = [
            '.gm-style-iw-a',
            '.gm-style-iw-c', 
            '.gm-style-iw-t',
            '.gm-style-iw-tc',
            '.gm-style-iw-d',
            '.gm-style-iw-chr',
            '.gm-fullscreen-control',
            '.gmnoprint',
            '.gm-bundled-control',
        ];
        document.querySelectorAll(gmSelectors.join(', ')).forEach(el => {
            // Solo los que están directamente en body, no dentro del contenedor del mapa
            if (el.closest('[id^="maps-"]') === null && el.closest('#map') === null) {
                el.remove();
            }
        });

        // 4. Variables globales de Google Maps
        const globalKeysToDelete = [
            '__googleMapsCallback',
            '__googleMapsScriptId', 
            '__ib__',
        ];
        globalKeysToDelete.forEach(key => {
            if ((window as any)[key] !== undefined) {
                delete (window as any)[key];
                console.log(`[purgeGoogleMaps] window.${key} eliminado`);
            }
        });

        // 5. El objeto google.maps — resetear el estado interno del Loader
        // NO eliminar window.google completo porque rompe otras cosas
        // Solo resetear el estado del Loader para que pueda re-inyectar estilos
        if ((window as any).google?.maps) {
            // Eliminar el callback interno que el Loader usa para detectar si ya cargó
            const googleMaps = (window as any).google.maps;
            
            // Resetear el estado interno del InfoWindow
            if (googleMaps.__gjsload__) delete googleMaps.__gjsload__;
            
            // Limpiar listeners globales acumulados
            if (googleMaps.event) {
                try {
                    googleMaps.event.clearListeners(window, 'load');
                } catch(e) {}
            }
        }

        // 6. Resetear la instancia singleton del Loader de @googlemaps/js-api-loader
        // El Loader guarda una instancia estática que impide re-inicialización
        try {
            const loaderInstance = (window as any).google?.maps?.loader;
            if (loaderInstance) {
                loaderInstance.done = false;
                loaderInstance.loading = false;
                loaderInstance.errors = [];
                console.log('[purgeGoogleMaps] Loader instance reseteado');
            }
        } catch(e) {}

        console.log('[purgeGoogleMaps] Limpieza completa ✓');
    }
}

(window as any).MapService = MapService;
export {MapService};